'use client';

import styles from './organization-hosted-events.module.scss';
import { EventCard, HorizontalEventCardSkeleton } from '@goodneighbor/ui';
import useInfiniteEventsQuery from '@/hooks/api/events/useInfiniteEventsQuery';
import { useState, useEffect } from 'react';
import EventSlideOut from '@/components/EventSlideOut';
import { EventWithOrganization } from '@/services/webApi';
import { parseForEventCardProps } from '@/services/event';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';

type SponsoredEventsSectionProps = {
  organizationId: string;
};

const SKELETON_COUNT = 3;

export function SponsoredEventsSection({
  organizationId
}: SponsoredEventsSectionProps) {
  const [selectedEvent, setSelectedEvent] =
    useState<EventWithOrganization | null>(null);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteEventsQuery({
    sponsor: organizationId,
    perPage: 6
  });

  const { ref: lazyLoadSentinelRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.1
  });

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <section className={styles.container}>
        <h2 className={styles.title}>Partnered Events</h2>
        <div className={styles.grid}>
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <HorizontalEventCardSkeleton key={i} />
          ))}
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section className={styles.container}>
        <h2 className={styles.title}>Partnered Events</h2>
        <p className={styles.message}>Failed to load partnered events.</p>
      </section>
    );
  }

  const events = data?.pages.flatMap((page) => page?.data?.events ?? []) ?? [];

  if (events.length === 0) {
    return null;
  }

  return (
    <section className={styles.container}>
      <h2 className={styles.title}>Partnered Events</h2>
      <div className={styles.grid}>
        {events.map((event) => (
          <EventCard
            key={event.id}
            {...parseForEventCardProps(
              event,
              event.clientName || event.locationName || ''
            )}
            onClick={() => setSelectedEvent(event as EventWithOrganization)}
            style={{ cursor: 'pointer' }}
          />
        ))}
        {isFetchingNextPage &&
          Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <HorizontalEventCardSkeleton key={`skeleton-${i}`} />
          ))}
      </div>
      <div ref={lazyLoadSentinelRef} style={{ height: '1px' }} />
      <EventSlideOut
        isOpen={!!selectedEvent}
        onOpenChange={() => setSelectedEvent(null)}
        event={selectedEvent}
      />
    </section>
  );
}
